import { Fragment } from 'react';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import arrowRight from '~/assets/icons/arrow_right.svg';
import tickCircle from '~/assets/icons/tick_circle.svg';
import { playSound, Sound } from '~/assets/sounds';
import { CurrencyIcon } from '~/components/collections';
import { ImgIcon } from '~/components/ui/ImgIcon';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import {
	Currency,
	VEHICLE_RIM_PAINT_TYPE_NAMES_PLURAL,
	VehicleRimPaintType,
} from '~/shared';
import {
	StyledArrowButton,
	StyledContainer,
	StyledCountItem,
	StyledCountMenu,
	StyledIconContainer,
	StyledInstalledContainer,
	StyledItemBadges,
	StyledItemImage,
	StyledItemTop,
	StyledMenuContainer,
	StyledPriceContainer,
	StyledShadow,
	StyledStockContainer,
} from './MenuItem.styled';

interface IMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
	type: 'menu' | 'item';
	name: string;
	icon: string | undefined;
	active?: boolean;
	price?: number;
	count?: number;
	paintType?: VehicleRimPaintType;
	stock?: number;
	width?: number;
	iconSize?: number;
	iconWidth?: number;
	iconOpacity?: number;
	noGlow?: boolean;
	unavailable?: boolean;
	installed?: boolean;
	onClick?: React.MouseEventHandler<HTMLElement>;
	variantIndex?: number;
	variantsCount?: number;
	onPrevVariant?: () => void;
	onNextVariant?: () => void;
	imageSlot?: React.ReactNode;
}

const MenuItem: React.FC<IMenuItemProps> = ({
	type,
	name,
	icon,
	active,
	price,
	count,
	paintType,
	stock,
	width,
	iconSize,
	iconWidth,
	iconOpacity = 1,
	noGlow = false,
	unavailable = false,
	installed = false,
	onClick,
	variantIndex: _variantIndex = 0,
	variantsCount = 0,
	onPrevVariant,
	onNextVariant,
	imageSlot,
	...props
}) => {
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const showVariantControls = variantsCount > 1;

	const handlePrevClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		playSound(Sound.ClickButton);
		onPrevVariant?.();
	};

	const handleNextClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		playSound(Sound.ClickButton);
		onNextVariant?.();
	};

	return (
		<StyledContainer
			$type={type}
			$active={active}
			$width={width}
			$unavailable={unavailable}
			$installed={installed}
			onClick={onClick}
			{...props}
		>
			{!noGlow && <StyledShadow />}
			{type === 'menu' && (
				<Fragment>
					<StyledMenuContainer>
						<StyledIconContainer $opacity={iconOpacity || 0.5}>
							<ImgIcon
								icon={icon || ''}
								width={iconWidth || iconSize || 60}
								height={iconSize || 60}
							/>
						</StyledIconContainer>
						<Text>{name}</Text>
					</StyledMenuContainer>
					{count ? (
						<StyledCountMenu>
							<Text>{count} шт.</Text>
						</StyledCountMenu>
					) : null}
				</Fragment>
			)}
			{type === 'item' && (
				<Fragment>
					<StyledItemTop>
						<Text>{name}</Text>
					</StyledItemTop>
					<StyledItemBadges>
						{paintType ? (
							<StyledCountItem>
								<Text>{VEHICLE_RIM_PAINT_TYPE_NAMES_PLURAL[paintType]}</Text>
							</StyledCountItem>
						) : null}
						{count && !unavailable && !installed ? (
							<StyledCountItem>
								<Text>{count} шт.</Text>
							</StyledCountItem>
						) : null}
					</StyledItemBadges>
					<StyledItemImage>
						{imageSlot ?? (
							<ImgIcon icon={icon || ''} width={100} height={100} />
						)}
					</StyledItemImage>
					{installed ? (
						<StyledInstalledContainer>
							<Text style={{ opacity: 0.5 }}>Запчасть установлена</Text>
							<ImgIcon icon={tickCircle} width={16} height={16} />
						</StyledInstalledContainer>
					) : unavailable ? (
						<StyledStockContainer>
							<Text style={{ opacity: 0.5 }}>Нет в наличии</Text>
						</StyledStockContainer>
					) : stock ? (
						<StyledStockContainer>
							<Text style={{ opacity: 0.5 }}>В наличии</Text>
							<Text style={{ fontWeight: 700 }}>
								{stock}
								<span style={{ opacity: 0.5, fontWeight: 400 }}> шт.</span>
							</Text>
						</StyledStockContainer>
					) : (
						<StyledPriceContainer>
							<Text style={{ opacity: 0.5 }}>Стоимость</Text>
							<Text style={{ fontWeight: 700 }}>
								{price?.toLocaleString('ru-RU')}
							</Text>
							<CurrencyIcon icon={rubleIcon} size={'md'} asMask />
						</StyledPriceContainer>
					)}
				</Fragment>
			)}
			{showVariantControls && type === 'item' && (
				<Fragment>
					<StyledArrowButton
						style={{ left: 20 }}
						onMouseEnter={() => playSound(Sound.LinkCursor)}
						onClick={handlePrevClick}
					>
						<ImgIcon icon={arrowLeft} width={12} height={12} />
					</StyledArrowButton>
					<StyledArrowButton
						style={{ right: 20 }}
						onMouseEnter={() => playSound(Sound.LinkCursor)}
						onClick={handleNextClick}
					>
						<ImgIcon icon={arrowRight} width={12} height={12} />
					</StyledArrowButton>
				</Fragment>
			)}
		</StyledContainer>
	);
};

export { MenuItem };
